import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Receipt, Truck, ScrollText, Coins } from "lucide-react";
import { useCustomerAuth, customerFetch } from "./CustomerAuthContext";

const DOC_ICONS = {
  Invoice: FileText,
  "Credit Note": Coins,
  "Debit Note": Coins,
  "Delivery Challan": Truck,
  "E-Way Bill": ScrollText,
  Receipt: Receipt,
};

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y,m,d]=String(iso).split("-"); return y&&m&&d?`${d}-${m}-${y}`:iso; };

export default function CustomerDashboard() {
  const { customer, token } = useCustomerAuth();
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    customerFetch(token)("/api/customer/sales-documents?page=1&page_size=5")
      .then((r) => r.json()).then(setRecent).catch(() => setRecent({ items: [], total: 0 }));
  }, [token]);

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Welcome, {customer?.name}</h1>
        <p className="text-sm text-slate-500 mt-1">Access all your BAISHALII sales documents and account information in one place.</p>
      </div>

      {/* Quick action tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/customer/sales-documents" data-testid="dash-tile-docs" className="group bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md hover:border-[#0a2a5e]/20 transition-all p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <FileText className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-[#0a2a5e]">Sales Documents</div>
              <p className="text-sm text-slate-500 mt-0.5">Invoices, credit notes, delivery challans, e-way bills and receipts.</p>
              <div className="text-xs text-[#0a2a5e] font-semibold mt-3 group-hover:underline">Open →</div>
            </div>
          </div>
        </Link>

        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md hover:border-[#0a2a5e]/20 transition-all p-6" data-testid="dash-tile-statement">
          <Link to="/customer/statement" className="flex items-start gap-4 group">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Coins className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-[#0a2a5e]">Account Statement</div>
              <p className="text-sm text-slate-500 mt-0.5">Live ledger, running balance and outstanding position.</p>
              <div className="text-xs text-[#0a2a5e] font-semibold mt-3 group-hover:underline">Open →</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent documents */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-[#0a2a5e]">Recent documents</h2>
          <Link to="/customer/sales-documents" className="text-xs font-semibold text-[#0a2a5e] hover:underline">View all →</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {!recent && <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>}
          {recent && recent.items?.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-slate-400">No documents yet. Your recent invoices and delivery notes will appear here.</div>
          )}
          {recent?.items?.map((d) => {
            const Icon = DOC_ICONS[d.doc_type] || FileText;
            return (
              <Link to="/customer/sales-documents" key={d.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50">
                <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{d.doc_type} · {d.doc_number}</div>
                  <div className="text-xs text-slate-500">{fmtDate(d.doc_date)} {d.po_number ? `· PO: ${d.po_number}` : ""}</div>
                </div>
                <div className="text-sm font-semibold text-slate-700 whitespace-nowrap">₹ {inr(d.amount)}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
